'use client';

import { setCookie } from 'cookies-next/client';
import { useAtomValue } from 'jotai/index';
import { useTranslations } from 'next-intl';
import {
  type ChangeEvent,
  type MouseEvent,
  useState,
} from 'react';

import { analyticEventSentUserMessage } from '@/features/analytic/enteties/analytic';
import createListingMessage from '@/features/listing/requests/create-listing-message';
import { loggedUserAtom } from '@/features/user/atoms/logged-user-atom';
import { type User } from '@/features/user/types/user-types';
import { useRouter } from '@/i18n/routing';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import { SECONDS } from '@/shared/constants/time-constants';
import Button from '@/ui/button/button';

import styles from './user-message.module.scss';

interface UserMessageProps {
  user: User,
  onSent?: () => void;
}

export default function UserMessage(props: UserMessageProps) {
  const {
    onSent,
    user,
  } = props;

  const router = useRouter();
  const listingMessageModalTranslations = useTranslations('listing.message');
  const loggedUser = useAtomValue(loggedUserAtom);

  const [message,
    setMessage] = useState('');
  const [isSuccessSent,
    setIsSuccessSent] = useState(false);
  const [isSending,
    setIsSending] = useState(false);

  const handleMessageChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  };

  const handleTextboxClick = () => {
    if (!loggedUser) {
      router.push(ROUTER_LINKS.phoneAuth);
    } else if (!loggedUser.verified) {
      router.push(ROUTER_LINKS.accountValidation);
    }

    // TODO: [auth-redirect]
  };

  const handleMessageSend = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!loggedUser) {
      setCookie('authData', JSON.stringify({ token: 'send-message', userName: user.firstName }), { maxAge: SECONDS.HOUR, path: '/' });
      router.push(ROUTER_LINKS.phoneAuth);
    } else if (!loggedUser.verified) {
      router.push(ROUTER_LINKS.accountValidation);
    }

    // if (!loggedUser?.verified) {
    //   router.push(ROUTER_LINKS.verification);
    //   return;
    // }

    setIsSending(true);

    createListingMessage({
      message,
      messageSrc: 'userprofile',
      recipientId: user.id,
    })
      .then(() => {
        setMessage('');
        setIsSuccessSent(true);
        analyticEventSentUserMessage();

        if (onSent) {
          onSent();
        }
      })
      .catch(() => null)
      .finally(() => {
        setIsSending(false);
      });
  };

  const messagePlaceholder = listingMessageModalTranslations('write-message');

  return (
    <div className={styles.listingMessage}>
      <textarea
        placeholder={`${messagePlaceholder}...`}
        className={styles.listingMessage_message}
        value={message}
        onChange={handleMessageChange}
        onClick={handleTextboxClick}
      />

      <div className={styles.listingMessage_sendMessage}>
        <Button
          element="button"
          size="md"
          fontSize="xs"
          onClick={handleMessageSend}
          fullWidth
          isLoading={isSending}
          isSuccess={isSuccessSent}
          successMessage={listingMessageModalTranslations('sent')}
        >
          {listingMessageModalTranslations('send-message')}
        </Button>
      </div>
    </div>
  );
}
